import { useAuthStore } from '@/stores/auth'
import { normalizeRole } from '@/utils/roles'

const normalise = value => String(value ?? '').trim().toLowerCase()

const extractRulePermissions = rule => {
  if (!rule)
    return []

  if (typeof rule === 'string')
    return [normalise(rule)]

  if (rule.name)
    return [normalise(rule.name)]

  const actions = Array.isArray(rule.action) ? rule.action : rule.action ? [rule.action] : []
  const subjects = Array.isArray(rule.subject) ? rule.subject : rule.subject ? [rule.subject] : []

  if (actions.length && subjects.length) {
    const combos = actions.flatMap(action => subjects.map(subject => normalise(`${action}_${subject}`)))
    const actionOnly = actions.map(action => normalise(action))

    return [...combos, ...actionOnly]
  }

  if (actions.length)
    return actions.map(action => normalise(action))

  return []
}

const getPermissionSet = () => {
  const authStore = useAuthStore()
  const rules = authStore.abilityRules || []
  const permissions = rules.flatMap(rule => extractRulePermissions(rule)).filter(Boolean)

  return new Set(permissions)
}

export const permissions = {
  can(permission, subject) {
    if (!permission)
      return true

    const permissionKey = subject
      ? normalise(`${permission}_${subject}`)
      : normalise(permission)

    return getPermissionSet().has(permissionKey)
  },

  canAny(permissionArray) {
    if (!Array.isArray(permissionArray) || !permissionArray.length)
      return true

    const permissionsSet = getPermissionSet()
    
    return permissionArray.some(permission => permissionsSet.has(normalise(permission)))
  },

  hasRole(role) {
    if (!role)
      return false

    const authStore = useAuthStore()

    const roles = [
      authStore.userRole,
      authStore.currentUser?.role,
      authStore.userProfile?.type,
    ]
      .filter(Boolean)
      .map(normalizeRole)

    return roles.includes(normalizeRole(role))
  },
}

export const can = (permission, subject) => permissions.can(permission, subject)
export const canAny = permissionArray => permissions.canAny(permissionArray)

export const canViewNavMenuGroup = item => {
  const children = Array.isArray(item?.children) ? item.children : []
  const hasAnyVisibleChild = children.some(child => can(child.action, child.subject))

  if (!(item?.action && item?.subject))
    return hasAnyVisibleChild

  return can(item.action, item.subject) && hasAnyVisibleChild
}
